import { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import { captureException } from '~/lib/sentry/captureException'

const TranslationContext = createContext()
const TranslationConsumer = TranslationContext.Consumer

const TranslationProvider = ({ children, translations: initialTranslations }) => {
  const ref = useRef()
  const translations = useMemo(() => {
    if (initialTranslations) {
      ref.current = initialTranslations
    }

    return ref.current
  }, [initialTranslations])

  const t = useCallback(
    (key, values = {}) => {
      if (!translations) {
        captureException('No translations passed', 'TranslationProvider', {
          extra: {
            translation: key,
          },
        })

        return key
      }

      const namespace = key.split(':')[0] || 'common'
      const propertyString = key.replace(`${namespace}:`, '')
      const properties = propertyString.split('.')

      if (Object.keys(values).includes('count')) {
        if (values.count !== 1) {
          properties.push(`${properties.pop()}_plural`)
        }
      }

      let translation = translations[namespace]
      for (let property of properties) {
        const newTrans = translation[property]

        if (!newTrans) {
          if (property.includes('_plural')) {
            // The 'count' value can also be used as a normal value instead for pluralization.
            // When the plural is not found we can fall back to the singular
            // If singular not found, fallback to key
            translation = translation[property.replace('_plural', '')] ?? propertyString

            break
          }

          console.error('No translation found', {
            key,
            translation,
            translations,
          })

          captureException('No translation found', 'TranslationProvider', {
            extra: {
              key,
              translation,
              translations,
            },
          })

          translation = propertyString
          break
        }

        translation = newTrans
      }

      Object.keys(values).forEach(key => {
        translation = translation.replaceAll(`{{${key}}}`, values[key])
      })

      return translation
    },
    [translations]
  )

  return <TranslationContext.Provider value={{ t }}>{children}</TranslationContext.Provider>
}

const useTranslation = () => useContext(TranslationContext)

export default TranslationContext

export { TranslationProvider, TranslationConsumer, useTranslation }
